import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  login as loginApi,
  logout as logoutApi,
  getCurrentUser,
} from "../api/authApi";
import type { LoginRequest, User } from "../api/authApi";
import { setAuthStore } from "../lib/apiClient";
import { removeRefreshToken } from "../utils/auth";

interface AuthState {
  isAuthenticated: boolean;
  isInitialized: boolean; // 초기 인증 체크 완료 여부
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  clearAuth: () => void;
  fetchUser: () => Promise<void>;
  initializeAuth: () => Promise<void>; // 앱 초기 로드 시 인증 상태 확인
}

type AuthStore = AuthState;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      // apiClient에 authStore 등록 (순환 참조 방지를 위해 함수 내부에서)
      setAuthStore({
        clearAuth: () => {
          get().clearAuth();
        },
      });

      return {
        isAuthenticated: false,
        isInitialized: false, // 초기화되지 않음 (항상 서버에서 확인 필요)
        user: null,

        // 앱 초기 로드 시 서버에서 인증 상태 확인
        initializeAuth: async () => {
          const currentState = get();

          // 이미 초기화되었으면 스킵 (중복 실행 방지)
          if (currentState.isInitialized) {
            return;
          }

          // 동시 실행 방지를 위해 즉시 isInitialized를 true로 설정
          set({ isInitialized: true });

          try {
            console.log("🔍 초기 인증 상태 확인 시작");
            // 서버에서 현재 사용자 정보 조회 (쿠키의 JWT 토큰 검증)
            const user = await getCurrentUser();
            console.log("✅ 인증 상태 확인 성공:", user);

            set({
              isAuthenticated: true,
              user,
            });
          } catch (error) {
            console.log("❌ 인증 상태 확인 실패 (로그인 필요):", error);
            // 401 에러 또는 기타 에러 시 로그인되지 않은 것으로 처리
            set({
              isAuthenticated: false,
              user: null,
            });
          }
        },

        login: async (email: string, password: string) => {
          try {
            const loginData: LoginRequest = { email, password };
            await loginApi(loginData);

            // 백엔드가 쿠키로 accessToken을 반환하므로 별도 저장 불필요
            // 쿠키는 브라우저가 자동으로 관리하고, withCredentials: true로 자동 전송됨

            // ⚠️ 핵심: 로그인 후 서버에서 실제로 인증이 성공했는지 확인
            // 쿠키가 설정되는 데 약간의 시간이 필요할 수 있으므로 짧은 지연 후 확인
            await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms 대기

            try {
              console.log("🔍 로그인 후 사용자 정보 조회 시작");
              const user = await getCurrentUser();
              console.log("✅ 사용자 정보 조회 성공:", user);

              // 서버에서 인증 확인 성공 - 로그인 성공
              console.log("🔄 인증 상태 업데이트 중...", {
                before: {
                  isAuthenticated: get().isAuthenticated,
                  hasUser: !!get().user,
                },
              });

              set({
                isAuthenticated: true,
                user,
                isInitialized: true,
              });

              console.log("✅ 인증 상태 업데이트 완료:", {
                after: {
                  isAuthenticated: get().isAuthenticated,
                  hasUser: !!get().user,
                  userId: get().user?.id,
                  userEmail: get().user?.email,
                },
              });
            } catch (error) {
              // ⚠️ 중요: getCurrentUser() 실패 시 로그인 실패로 처리
              const isAxiosError =
                error && typeof error === "object" && "isAxiosError" in error;
              const status =
                isAxiosError && "response" in error
                  ? (error as any).response?.status
                  : undefined;
              const responseData =
                isAxiosError && "response" in error
                  ? (error as any).response?.data
                  : undefined;

              console.error("❌ 로그인 후 인증 확인 실패:", {
                error,
                message: error instanceof Error ? error.message : String(error),
                isAxiosError,
                status,
                responseData,
                url:
                  isAxiosError && "config" in error
                    ? (error as any).config?.url
                    : undefined,
                fullUrl:
                  isAxiosError && "config" in error
                    ? `${(error as any).config?.baseURL}${
                        (error as any).config?.url
                      }`
                    : undefined,
              });

              // 상태를 초기화
              set({
                isAuthenticated: false,
                user: null,
                isInitialized: true,
              });

              // 원본 에러를 그대로 throw
              throw error;
            }
          } catch (error) {
            console.error("Login failed:", error);
            throw error;
          }
        },

        logout: async () => {
          try {
            // 백엔드로 로그아웃 요청 (쿠키 삭제)
            await logoutApi();
          } catch (error) {
            console.error("로그아웃 API 호출 실패:", error);
            // API 호출 실패해도 프론트엔드 상태는 초기화
          } finally {
            // 프론트엔드 상태 초기화
            get().clearAuth();
          }
        },

        setUser: (user: User) => {
          set({ user });
        },

        clearAuth: () => {
          // 쿠키는 서버에서 삭제하거나 만료되므로 클라이언트에서 직접 삭제 불가
          // httpOnly 쿠키는 JavaScript로 접근 불가
          // sessionStorage의 refreshToken은 제거
          removeRefreshToken();

          set({
            isAuthenticated: false,
            user: null,
            // isInitialized는 유지 (이미 초기화되었으므로)
          });
        },

        fetchUser: async () => {
          try {
            const user = await getCurrentUser();
            set({
              user,
              isAuthenticated: true,
              isInitialized: true,
            });
          } catch (error) {
            console.error("Failed to fetch user:", error);
            get().clearAuth();
            throw error;
          }
        },
      };
    },
    {
      name: "auth-storage", // localStorage 키
      partialize: () => ({
        // isAuthenticated와 user는 저장하지 않음
        // 매번 앱 시작 시 서버에서 인증 상태를 확인해야 함
        // localStorage에 저장하면 서버 상태와 불일치할 수 있음
      }),
    }
  )
);
